<template>
  <div class="basic-view">
    <div class="basic-content">
      <div class="basic-col">
        <KeyboardDebounce v-if="isShowKeyboardDebounce" />
        <DPI v-if="isShowDpi" />
        <touchGuard v-if="isShowTouchGuard" />
        <template v-if="specialCheckCurrent(speciaBehiver.DeviceOfRoyuan)">
          <SleepTime24g v-if="isShowSleepTime" />
          <SleepTimeBt v-if="isShowBTSleepTime" />
        </template>
        <RtStab v-if="shouldMoveRtToLeft && isShowRTStab" />
        <StabilityTime v-if="isShowStabilityTime" />
      </div>
      <div v-if="isShowBaseViewLine" class="basic-line"></div>
      <div class="basic-col">
        <Win v-if="isShowLockWin" />
        <!-- <Lock_keyboard v-if="isShowLockKeyboard" />-->
        <!-- <Direction_key_swap v-if="isShowDirectionKeySwap" /> -->
        <KeyMode v-if="isShowKeyMode" />
        <!-- <Baswcklight_close v-if="isShowKeyModeSwitch" /> -->
        <!-- <Sideled_close v-if="[DevicePid.IROKRA68_1].includes(DeviceManager.instance.state.currentPid)" />  -->
        <Rate v-if="isShowRate" />
        <SuperResponse v-if="isSuperResponse" />
        <RtStab v-if="!shouldMoveRtToLeft && isShowRTStab" />
        <Cancel_default_deadzone v-if="isShowCancelDefaultDeadzone" />
        <template v-if="!specialCheckCurrent(speciaBehiver.DeviceOfRoyuan)">
          <SleepTime v-if="isShowSleepTime" />
        </template>
      </div>
    </div>
    <div class="basic-footer" :class="{ 'only-reset': isOnlyReset }">
      <div class="reset-wrapper">
        <Reset />
        <div class="reset_des" v-if="!DeviceManager.instance.state.currentDevice.IsSupportMagnet">
          <div class="notice-icon">
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2" />
              <path d="M12 8V12" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
              <circle cx="12" cy="16" r="1" fill="currentColor" />
            </svg>
          </div>
          <span>{{ $t('reset_notice') }}{{ getCurrentDevice.resetDes.length > 0 ? $t(getCurrentDevice.resetDes) : '' }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref, computed, defineAsyncComponent } from 'vue'

const SleepTime = defineAsyncComponent(() => import('./sleep_time.vue'))
const SleepTime24g = defineAsyncComponent(() => import('./sleep_time_24g.vue'))
const SleepTimeBt = defineAsyncComponent(() => import('./sleep_time_bt.vue'))
const DPI = defineAsyncComponent(() => import('./DPI.vue'))
const Win = defineAsyncComponent(() => import('./win.vue'))
const Rate = defineAsyncComponent(() => import('./rate.vue'))
const SuperResponse = defineAsyncComponent(() => import('./superResponse.vue'))
const RtStab = defineAsyncComponent(() => import('./rt_stab.vue'))
const touchGuard = defineAsyncComponent(() => import('./touch_guard.vue'))
const KeyboardDebounce = defineAsyncComponent(() => import('./keyboard_debounce.vue'))
const StabilityTime = defineAsyncComponent(() => import('./stability_time.vue'))
const KeyMode = defineAsyncComponent(() => import('./key_mode.vue'))
const Reset = defineAsyncComponent(() => import('../operation_config/reset.vue'))
const Cancel_default_deadzone = defineAsyncComponent(() => import('./cancel_default_deadzone.vue'))
import {
  isShowDpi,
  isShowLockWin,
  isShowRate,
  isShowSuperResponse as isSuperResponse,
  isShowSleepTime,
  isShowBTSleepTime,
  isShowRTStab,
  isShowTouchGuard,
  isShowKeyboardDebounce,
  isShowStabilityTime,
  isShowKeyMode,
  shouldMoveRtToLeft,
  isShowBaseViewLine,
} from '../../define/showConfig'
import { speciaBehiver, specialCheckCurrent } from '@/define/limit'
import { RoYuanDeviceList, HuochaiDevice } from '@/define/device_vendor'
import { DeviceManager } from '@/service/device'
import firmware from '@/upgrade/firmware'
import { getCurrentDevice } from '@/profiles/selector'

const isOnlyReset = computed(() => {
  return (
    !isShowDpi.value &&
    !isShowLockWin.value &&
    !isShowRate.value &&
    !isSuperResponse.value &&
    !isShowSleepTime.value &&
    !isShowBTSleepTime.value &&
    !isShowRTStab.value &&
    !isShowTouchGuard.value &&
    !isShowKeyboardDebounce.value &&
    !isShowStabilityTime.value &&
    !isShowKeyMode.value
  )
})

const initShowRate = async () => {
  if (RoYuanDeviceList.includes(DeviceManager.instance.state.currentPid)) {
    const isV304 = await firmware.versionRoYuanV304()
    isShowRate.value = isV304 && getCurrentDevice.value.rate?.length > 0
  } else {
    isShowRate.value = getCurrentDevice.value.rate?.length > 0
  }
}

const isShowCancelDefaultDeadzone = ref<boolean>(false)
const initCancelDefaultDeadzone = async () => {
  if (HuochaiDevice.includes(DeviceManager.instance.state.currentPid)) {
    isShowCancelDefaultDeadzone.value = await firmware.isHuoChaiRenCancelDefaultDeadzone()
  }
}
onMounted(() => {
  initShowRate()
  initCancelDefaultDeadzone()
})
</script>

<style scoped lang="less">
.basic-view {
  display: flex;
  flex-direction: column;
  flex: 0 0 auto;
  width: 100%;
  min-width: 0;
  max-width: 100%;
  min-height: 0;
  border-radius: var(--radius-l);
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-sizing: border-box;
  padding: 40px 40px 20px 40px;
  position: relative;
  gap: 0;
  transition: transform 0.4s cubic-bezier(0.23, 1, 0.32, 1);
  outline: none;
  background: transparent;
  overflow: hidden;

  /* 核心修复：使用伪元素实现毛玻璃，解决弹窗 trapping 问题 */
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: var(--glass-panel-bg);
    backdrop-filter: var(--glass-panel-blur);
    border-radius: inherit;
    z-index: -1;
    pointer-events: none;
  }

  .basic-content {
    display: flex;
    flex: 1;
    width: 100%;
    min-width: 0;
    min-height: 0;
  }

  .basic-footer {
    width: 100%;
    margin-top: 28px;
    display: flex;
    justify-content: flex-end;
    align-items: flex-end;
    transition: all 0.4s var(--transition-base);

    &.only-reset {
      flex: 1;
      margin-top: 0;
      justify-content: center;
      align-items: center;

      .reset-wrapper {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 20px;
        padding: 36px 32px;
        background: rgba(var(--color-brand-1-rgb), 0.03);
        border-radius: var(--radius-l);
        border: 1px dashed rgba(var(--color-brand-1-rgb), 0.15);
        width: min(100%, 500px);
        text-align: center;
        transition: all 0.4s var(--transition-base);

        &:hover {
          background: rgba(var(--color-brand-1-rgb), 0.05);
          border-color: rgba(var(--color-brand-1-rgb), 0.3);
          transform: translateY(-4px);
        }

        :deep(.mgx_action_btn) {
          transform: scale(1.03);
          padding: 11px 28px;
          &:hover {
            transform: scale(1.06) translateY(-2px);
          }
        }

        .reset_des {
          margin-top: 0;
          color: var(--brand-color-pure);
          font-weight: 600;
          font-size: 14px;
          line-height: 1.5;
          opacity: 0.9;
          display: flex;
          align-items: flex-start;
          gap: 8px;
          max-width: 400px;

          .notice-icon {
            width: 18px;
            height: 18px;
            display: flex;
            align-items: center;
            justify-content: center;
            color: var(--brand-color-pure);
            margin-top: 1px;
          }
        }
      }
    }

    .reset-wrapper {
      display: flex;
      flex-direction: row;
      align-items: center;
      justify-content: flex-end;
      flex-wrap: nowrap;
      gap: 8px;
      max-width: 100%;
    }

    .reset_des {
      margin-top: 0;
      font-size: 12px;
      color: var(--color-text-hint);
      line-height: 1.4;
      background: transparent;
      padding: 0;
      border-radius: 0;
      display: flex;
      align-items: center;
      gap: 6px;
      max-width: none;
      flex: 0 1 auto;
      opacity: 0.88;

      .notice-icon {
        flex-shrink: 0;
        width: 12px;
        height: 12px;
        margin-top: 0;
        color: var(--color-text-hint);
        opacity: 0.55;
      }
    }
  }
}

.basic-col:has(*) {
  flex: 1;
  min-width: 0;
}
.basic-col > * {
  margin-bottom: 24px;
}
.basic-col > *:last-child {
  margin-bottom: 0;
}
.basic-line {
  width: 1px;
  background: rgba(0, 0, 0, 0.05);
  height: auto;
  margin: 0 40px;
}

.basic-reset {
  justify-content: flex-start;
  margin-top: 24px;
}

@media @y-md {
  .basic-view {
    padding: 28px 28px 18px;

    .basic-footer {
      margin-top: 22px;

      &.only-reset {
        .reset-wrapper {
          gap: 18px;
          padding: 30px 24px;
        }
      }

      .reset-wrapper {
        gap: 8px;
        max-width: 100%;
      }

      .reset_des {
        font-size: 12px;
      }
    }
  }

  .basic-col > * {
    margin-bottom: 18px;
  }

  .basic-line {
    margin: 0 28px;
  }
}

@media @y-sm {
  .basic-view {
    padding: 22px 22px 16px;

    .basic-footer {
      margin-top: 16px;
      justify-content: center;

      &.only-reset {
        .reset-wrapper {
          gap: 16px;
          padding: 24px 20px;
        }
      }

      .reset-wrapper {
        width: 100%;
        max-width: 100%;
        flex-direction: column;
        align-items: stretch;
      }

      .reset_des {
        max-width: 100%;
        flex-basis: auto;
        white-space: normal;

        span {
          white-space: normal;
        }
      }
    }
  }

  .basic-col > * {
    margin-bottom: 14px;
  }

  .basic-line {
    margin: 0 20px;
  }
}
</style>
